import {
  Button,
  FormControl, FormLabel,
  Grid, Select, Spinner, Stack, Text
} from '@chakra-ui/react'
import type { AddonProductEnum, AvailableAddonOfferInsurerRisk, InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { RiskVariant, SelectionType } from 'BrokerTsLib/src/enums'
import { useForm } from 'BrokerTsLib/src/rhf'
import { addMonths, format } from 'date-fns'
import { useEffect, useState } from 'react'

import { useAuth } from '@/context/AuthContext'
import { useSelectedOffersAddonContext } from '@/context/SelectedOffersAddonProvider'
import { useGetOfferDocuments } from '@/hooks/useDocuments'
import { usePriceFormat } from '@/hooks/usePriceFormat'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { FormRow } from '../../containers/forms/FormRow'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../context/OffersAddonProvider'
import { useSelections } from '../../hooks'
import { useChangeParams, useGetAddonProductEnums, useGetAddonRiskVariantProducts, useGetAvailableAddonOffers, useSelectOfferAddon, useSelectVariant, useSetAddonSelectedVariant } from '../../hooks/useOfferAddon'
import { CompanyLogo } from '../CompanyLogo'
import { SectionItem, SidebarSection } from '../RecapSummary/RecapSidebar'
import { RecapExtraRow } from './RecapExtraRow'

const getParamsDataArr = (values: any) =>
  Object.keys(values).map((key) => {
    return { name: key, value: values[key] }
  })

export const RecapExtraRowWrapper = ({ risk }: { risk: AvailableAddonOfferInsurerRisk }) => {
  const { translate } = useTranslationsContext()
  const { priceFormat } = usePriceFormat()

  const { insuranceParams: ip, refetchInsuranceParams, insuranceParamsId } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const riskVariantId = risk.riskVariants[0].riskVariantId
  const [offer, setOffer] = useState(risk.riskVariants[0].offer) // zatim vzdy je pouze jedna riskVarianta

  const { recalculateOffers: recalculateAddonOffers, calculatingOffersIds: calculatingAddonOffersIds } = useOffersAddonContext()
  const isRecalculatingAddonOffer = calculatingAddonOffersIds?.includes(offer!.offerId) ?? false

  const { data: addonRiskVariantProducts, mutateAsync: getAddonRiskVariantProducts } = useGetAddonRiskVariantProducts()
  const { mutateAsync: getAvailableAddonOffers, isPending: isLoadingAvailableAddonOffers } = useGetAvailableAddonOffers()

  const [addonProductEnums, setAddonProductEnums] = useState<AddonProductEnum[]>([])
  const { mutateAsync: getAddonProductEnums, isPending: isLoadingAddonProductEnums } = useGetAddonProductEnums(setAddonProductEnums)
  const { mutateAsync: setAddonSelected } = useSetAddonSelectedVariant()
  const { isPending: isLoadingSelectofferAddon } = useSelectOfferAddon()
  const { refetch: refetchSelectedOfferAddons } = useSelectedOffersAddonContext()

  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const pfEnum = dataPf?.data ?? []

  const [selected, setSelected] = useState(offer.isActive)

  useEffect(() => {
    getAddonRiskVariantProducts({
      offerId: offer?.offerId,
      riskVariantId: offer?.riskVariantId,
    })

    getAddonProductEnums({
      offerId: offer?.offerId,
      riskVariantId: offer?.riskVariantId,
      changedParams: []
    })
  }, [])

  const { mutateAsync: selectVariant } = useSelectVariant()
  const { mutateAsync: changeParams } = useChangeParams()

  // konverze z pole na object, aby mohly byt hodnoty pouzity ve formulari
  const defaultValues = offer.params?.parameterValues.reduce((obj: any, c) => {
    return { ...obj, [c.name]: c.value }
  }, {})

  const {
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({ defaultValues: defaultValues })

  const changedParams = getParamsDataArr(getValues()).filter(
    (x) => {
      const index = offer.params?.parameterValues.findIndex((y) => y.name == x.name && y.value == x.value) ?? -1
      return index < 0
    }
  ) ?? []

  // přenačte aktuální doplňkovou offeru
  const reloadCurrentAvailableAddonOffer = () => {
    return new Promise((resolve, reject) => {
      getAvailableAddonOffers({ insurerId: offer.insurerId, riskVariantId: riskVariantId, paramsId: insuranceParamsId! })
        .then(({ data }) => {
          const newOffer = data
            .find(x => x.insurerId == offer.insurerId)
            ?.risks.find(x => x.riskId == risk.riskId)
            ?.riskVariants.find(x => x.riskVariantId == riskVariantId)
            ?.offer

          if (newOffer)
            setOffer(newOffer)

          resolve(data)
        })
        .catch((err) => reject(err))

    })
  }

  // vybver varianty
  const handleChangeVariant = (productId: string) => {
    selectVariant({ activeOfferId: offer.offerId, productId: parseInt(productId) })
      .then((data) =>
        getAddonProductEnums({
          offerId: data.data,
          riskVariantId: offer?.riskVariantId,
          changedParams: []

        }).then(() =>
          reloadCurrentAvailableAddonOffer()
        )
      )
  }

  // vyber parametru jineho nez varianty
  const handleChangeParams = (paramName: string, paramValue: string) => {
    setValue(paramName, paramValue)

    getAddonProductEnums({
      offerId: offer?.offerId,
      riskVariantId: offer?.riskVariantId,
      changedParams: changedParams
    })
  }

  const submit = handleSubmit((values) => {
    return new Promise((resolve, reject) => {
      // ulozim zmenu parametru
      changeParams({
        activeOfferId: offer.offerId,
        parameterValues: getParamsDataArr(values),
      })
        .then((resp) => {
          // vynutim rekalkulaci aktivni offery.
          // update v addonOffers se provede automaticky pres signalR
          recalculateAddonOffers([offer.offerId], true)
            .then(() => {
              reloadCurrentAvailableAddonOffer()
                .then(() => resolve(resp))
            })
        })
        .catch((err) => {
          reject(err)
        })
    })
  })


  // text headru
  let headerName: React.ReactNode | string
  switch (offer.riskVariantId) {
    case RiskVariant.AddonGAP:
    {
      const riskName = 'Garance pořizovací ceny vozidla'
      headerName = riskName

      break
    }
    default:
      headerName = offer.riskVariantName
      break
  }

  // Check / uncheck doplnku
  const handleSelectAddonOffer = (value: boolean) => {
    setSelected(value)

    setAddonSelected({ activeOfferId: offer!.offerId, selected: value })
      .then(() => {
        refetchInsuranceParams()
        refetchSelectedOfferAddons({})
      })
  }


  let addonInsuranceStartsAt: Date | undefined
  if (offer.riskVariantId !== RiskVariant.AddonLegalProtection)
    addonInsuranceStartsAt = (insuranceParams?.generalParams.insuranceStartDate ?? undefined) && new Date(insuranceParams.generalParams.insuranceStartDate!)

  const anyChangedParams = changedParams.length > 0

  let addonInsuranceEndsAt: Date | undefined
  if (addonInsuranceStartsAt) {
    const changedTerm = changedParams.find(x => x.name == 'Term')?.value
    if (changedTerm) {
      addonInsuranceEndsAt = addMonths(addonInsuranceStartsAt, parseInt(changedTerm ?? offer.params?.parameterValues.find(x => x.name == 'Term')?.value ?? '0'))
    }
  }

  const { refetch: getOfferDocuments, data: offerDocuments, isLoading: isLoadingDocs } = useGetOfferDocuments(offer!.offerId, { enabled: false, retry: false })
  const xmlDocs = offerDocuments?.data.filter(x => x.name !== 'OfferDocument')

  const { user } = useAuth()
  useEffect(() => {
    if (user?.enableOfferXml)
      setTimeout(() => getOfferDocuments(), 2000)
  }, [offer.offerId, isRecalculatingAddonOffer, user?.enableOfferXml])

  const hardErrors = offer.errors.filter(x => !x.softError) ?? []

  return (
    <RecapExtraRow
      name={headerName}
      checkboxLabel={offer.name!}
      isEnabled={selected && hardErrors.length == 0}
      isDisabled={hardErrors.length > 0}
      onEnabledChange={handleSelectAddonOffer}
      helpText={offer.productDescription}
      price={anyChangedParams ? '' : priceFormat(offer.pricePerYear) + ' / rok'}
      documents={xmlDocs}
      sidebar={
        <SidebarSection fullHeight>
          {!selected &&
            <Grid
              sx={{
                w: '100%',
                h: '100%',
                placeContent: 'center',
                placeItems: 'center',
              }}
            >
              <Text sx={{ fontSize: 'lg' }}>Produkt není vybrán</Text>
            </Grid>
          }

          {selected &&
            <>
              <SidebarSection
                sx={{
                  placeContent: 'center',
                  placeItems: 'center'
                }}
                isFirst
              >
                <CompanyLogo insurer={{ id: offer.insurerId, name: offer.insurerName }} offerType={offer.type} p={8} maxW='240px' />
              </SidebarSection>

              <SectionItem label="Splátky">
                <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                  {pfEnum.find(x => x.id === offer.paymentFrequencyId)?.description}
                </Text>
              </SectionItem>

              <SectionItem label="Splátka pojistného">
                <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                  {anyChangedParams ? 'N/A' : priceFormat(offer.priceFrequency)}
                </Text>
              </SectionItem>

              {offer.riskVariantId == RiskVariant.AddonLegalProtection &&
                <SectionItem label="Počátek pojištění">
                  <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                    Od přijetí platby
                  </Text>
                </SectionItem>
              }
              {addonInsuranceStartsAt &&
                <>
                  <SectionItem label="Počátek pojištění">
                    <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                      {format(addonInsuranceStartsAt, 'd. MMMM yyyy')}
                    </Text>
                  </SectionItem>

                  {addonInsuranceEndsAt &&
                    <SectionItem label="Konec pojištění">
                      <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                        {format(addonInsuranceEndsAt, 'd. MMMM. yyyy')}
                      </Text>
                    </SectionItem>
                  }
                </>
              }
            </>}
        </SidebarSection>
      }
    >
      {offer.errors.length > 0 &&
        <Stack>
          {offer.errors
            .sort((a, b) => (!a.softError ? 1 : 0) - (!b.softError ? 1 : 0))
            .map(e => (
              <Text key={e.message} variant={e.softError ? 'warning' : 'danger'}>{e.message}</Text>
            ))}
        </Stack>
      }

      {selected &&
        <>
          <FormRow itemsCount={3} sx={{ rowGap: 4, columnGap: 4 }}>
            {addonRiskVariantProducts?.data && addonRiskVariantProducts.data.length > 0 && (
              <FormControl>
                <FormLabel>Varianta</FormLabel>

                <Select
                  id="variant"
                  variant='outline'
                  value={offer.productId.toString()}
                  disabled={addonRiskVariantProducts?.data.length == 1}
                  onChange={(e) => {
                    handleChangeVariant(e.target.value)
                  }}
                >
                  {addonRiskVariantProducts?.data.map((x) => (
                    <option key={x.id} value={x.id.toString()}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {addonProductEnums.map((x) => (
              <FormControl key={x.paramName}>
                <FormLabel>{x.paramDescription}</FormLabel>

                <Select
                  id={x.paramName}
                  variant='outline'
                  icon={isLoadingAddonProductEnums || isLoadingAvailableAddonOffers || isLoadingSelectofferAddon
                    ? <Spinner />
                    : undefined
                  }
                  value={watch(x.paramName)?.toString()}
                  disabled={x.values.length == 1}
                  onChange={(e) => handleChangeParams(x.paramName, e.target.value)}
                >
                  {x.values?.map((product) => (
                    <option key={product.value} value={product.value.toString()}>
                      {product.text}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ))}

            {anyChangedParams && (
              <SectionItem sx={{ justifyContent: 'center', gridColumn: '1/-1' }}>
                <Button
                  variant="solid"
                  isDisabled={false}
                  isLoading={isSubmitting}
                  loadingText="Přepočítávám..."
                  onClick={() => submit()}
                  gridColumnStart={1}
                >
                  {translate('recalculate', 'Přepočítat')}
                </Button>
              </SectionItem>
            )}
          </FormRow>
        </>
      }
    </RecapExtraRow>
  )
}